import { VariationLineItem } from '@/helpers/types';

/**
 * Helper function to create a complete product object for backend API
 */
export const createProductPayload = (
  lineItem: VariationLineItem,
  sectionId: string,
): any => {
  const currentDateTime = new Date()
    .toISOString()
    .replace('T', ' ')
    .split('.')[0];
  const currentDate = new Date().toISOString().split('T')[0];

  return {
    uuid: lineItem.id,
    section_uuid: sectionId,
    product_id: lineItem.product_id || lineItem.product_uuid || null,
    supplier_id: lineItem.supplier_id || null,
    item_type: lineItem.item_type !== undefined ? lineItem.item_type : 1,
    item_type_label: lineItem.item_type_label || (lineItem.item_type === 0 ? 'Service' : 'Product'),
    item_code: lineItem.item_code || null,
    manufacture_number: lineItem.manufacture_number || null,
    part_number: lineItem.part_number || null,
    barcode: lineItem.barcode || null,
    entered_date: lineItem.entered_date || currentDate,
    name: lineItem.name,
    description: lineItem.description,
    attachment_url: lineItem.attachment_url || null,
    unit_cost: lineItem.unit_cost || lineItem.cost || 0,
    unit_price: lineItem.unit_price || lineItem.price || 0,
    quantity: lineItem.quantity || 0,
    reserved_quantity_main: lineItem.reserved_quantity_main || 0,
    reserved_quantity_van: lineItem.reserved_quantity_van || 0,
    total_reserved_quantity: lineItem.total_reserved_quantity || 0,
    total: lineItem.total || 0,
    markup: lineItem.markup || 0,
    tax: lineItem.tax || 0,
    discount: lineItem.discount || 0,
    display_order: lineItem.display_order || 0,
    created_at: lineItem.created_at || currentDateTime,
    updated_at: lineItem.updated_at || currentDateTime,
  };
};

/**
 * Helper function to transform backend product data to frontend line item
 */
export const transformBackendProductToLineItem = (
  product: any,
): VariationLineItem => {
  const quantity = product.quantity || 0;
  const unitPrice = product.unit_price || product.price || 0;
  const markup = product.markup || 0;
  const tax = product.tax || 0;
  const discount = product.discount || 0;

  return {
    id: product.uuid || product.id,
    name: product.name || product.product_name || '',
    description: product.description || '',
    quantity,
    unit_cost: product.unit_cost || 0,
    unit_price: unitPrice,
    cost: product.unit_cost || 0, // Alias for unit_cost
    price: unitPrice, // Alias for unit_price
    markup,
    tax,
    discount,
    total:
      typeof product.total === 'string'
        ? parseFloat(product.total)
        : Number(product.total || 0),
    item_type: product.item_type !== undefined ? product.item_type : 1,
    item_type_label: product.item_type_label || 'Product',
    item_code: product.item_code,
    manufacture_number: product.manufacture_number,
    part_number: product.part_number,
    barcode: product.barcode,
    entered_date: product.entered_date,
    attachment_url: product.attachment_url,
    product_id: product.product_id,
    supplier_id: product.supplier_id,
    reserved_quantity_main: product.reserved_quantity_main || 0,
    reserved_quantity_van: product.reserved_quantity_van || 0,
    total_reserved_quantity: product.total_reserved_quantity || 0,
    display_order: product.display_order || 0,
    created_at: product.created_at,
    updated_at: product.updated_at,
    stockAvailabilityMain: product.reserved_quantity_main || 0, // Map for compatibility
    stockAvailabilityVan: product.reserved_quantity_van || 0, // Map for compatibility
    pickQuantityMain: product.reserved_quantity_main || 0, // Map for compatibility
    pickQuantityVan: product.reserved_quantity_van || 0, // Map for compatibility
    isCustom: product.item_type === 0, // If item_type is 0, it's a service/custom item
  };
};

/**
 * Calculate totals for a section based on its line items
 */
export const calculateSectionTotals = (lineItems: VariationLineItem[]) => {
  return {
    total_items: lineItems.length,
    subtotal: lineItems.reduce((sum, item) => sum + (item.total || 0), 0),
    cost: lineItems.reduce(
      (sum, item) => sum + (item.unit_cost || item.cost || 0),
      0,
    ),
  };
};
